import React, { useMemo } from 'react';
import { Dot } from '@ygtechnologies/rubus-ui';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faImage } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';

import theme from '../components/RoomOptionTwoTierList/RoomOptionTwoTierList.module.scss';
import { ConfiguratorPresetGroupDto } from '@/services/configurator';
import { BuyerCurrencyDisplay } from '@/buyer/components/BuyerCurrencyDisplay';
import { useImageUrls } from '../../useImageUrls';

interface StyleGroupRowProps {
    styleGroup: ConfiguratorPresetGroupDto;
    onClick: () => void;
}

// A single style-group entry in the room's Styles section — mirrors the option `GroupRow` (same
// visual classes): thumbnail of the picked style, group name, current selection line, and a chevron.
// Clicking opens the style-selection modal instead of a tier-2 option view.
export const StyleGroupRow: React.FC<StyleGroupRowProps> = ({ styleGroup, onClick }) => {
    const { getOptionPresetImageUrl } = useImageUrls();
    const pickedStyle = styleGroup.styles.find((s) => s.isPicked) ?? null;
    const selected = !!pickedStyle;
    const imageId = pickedStyle?.images[0]?.id ?? null;
    const src = useMemo(
        () => (imageId != null ? getOptionPresetImageUrl(imageId) : undefined),
        [imageId, getOptionPresetImageUrl]
    );

    return (
        <button type="button" className={theme.groupRow} onClick={onClick}>
            <div className={theme.imageButton}>
                <Thumbnail
                    className={theme.thumbnail}
                    src={src}
                    alt={pickedStyle?.name ?? styleGroup.name}
                    fallback={
                        <div className={theme.thumbnail} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faImage} />
                        </div>
                    }
                />
            </div>
            <div className={theme.info}>
                <div className={theme.topLine}>
                    <span className={theme.name} title={styleGroup.name}>
                        {styleGroup.name}
                    </span>
                    <FontAwesomeIcon className={theme.chevron} icon={faChevronRight} />
                </div>
                <div className={theme.product} title={pickedStyle?.name ?? undefined}>
                    <span className={theme.productLabel}>
                        <Dot variant={selected ? 'primary' : 'warning'} />
                        <span className={theme.productName}>
                            {selected ? pickedStyle?.name : <Trans>None selected</Trans>}
                        </span>
                    </span>
                </div>
                <div className={theme.price}>
                    {pickedStyle && <BuyerCurrencyDisplay value={pickedStyle.price} />}
                </div>
            </div>
        </button>
    );
};
